import { Transform } from '@ikea-aoa/elements-3d';
import {
	ConnectionManager,
	SceneObject,
	TransformVector,
	ConnectionRelation,
	ConnectableObject,
	GridTools,
	ProductData,
} from '@ikea-aoa/component-view-3d';
import { RangeSettings } from 'sofa-reborn-react';
import { ModuleConnectionManager } from '../../util/SceneUtils/ModuleConnectionManager';

export class SmartMirrorManager {
	private connectionManager: ConnectionManager;
	private articleMirrorMapping: Record<string, string>;

	constructor(
		public moduleManager: ModuleConnectionManager,
		private sceneItems: Map<string, ConnectableObject>,
		private gridTools: GridTools,
		private productData: ProductData,
		private rangeSettings: RangeSettings
	) {
		this.connectionManager = this.moduleManager.connectionManager;
		this.articleMirrorMapping = Object.values(this.productData).reduce((mapping, product) => {
			if (this.rangeSettings.smartMirrorModules?.includes(product.modelId)) {
				const mirrorModelId = product.modelId.includes('MiRROR')
					? product.modelId.replace('_MIRROR', '')
					: product.modelId + '_MIRROR';
				const inversProductId = Object.values(this.productData).find(
					(mirrorProduct) =>
						mirrorProduct.filter.color === product.filter.color &&
						mirrorProduct.modelId === mirrorModelId
				)?.productId;
				if (inversProductId) {
					mapping[product.productId] = inversProductId;
					mapping[inversProductId] = product.productId;
				}
			}
			return mapping;
		}, {});
	}

	public shouldMirror(sceneObject: SceneObject | null): boolean {
		const distance = this.getDistance(sceneObject);
		if (distance) {
			return distance < 8;
		}
		return false;
	}

	public isSmartMirroring(
		sceneObject: SceneObject | null,
		smartMirrorModules: string[] | undefined
	): boolean {
		if (smartMirrorModules && sceneObject) {
			return this.isSmartMirrorArticle(sceneObject.articleId, smartMirrorModules);
		}
		return false;
	}

	public getMirrorArticle(sceneObject: SceneObject | null): string | null {
		if (sceneObject !== null) {
			return this.getMirrorArticleWithArticleId(sceneObject.articleId);
		}
		return null;
	}

	public getMirrorArticleWithArticleId(articleId: string): string {
		return this.articleMirrorMapping[articleId];
	}

	public isSmartMirrorArticle(
		articleId: string,
		smartMirrorModules: string[] | undefined
	): boolean {
		if (smartMirrorModules) {
			const sceneItemModel = this.connectionManager.getModelData(articleId);
			return (
				sceneItemModel &&
				(sceneItemModel.modelId.includes('MIRROR') ||
					smartMirrorModules.includes(sceneItemModel.modelId))
			);
		}
		return false;
	}

	private getDistance(sceneObject: SceneObject | null): number | undefined {
		const connections = new Array<number>();
		const articleId = this.getMirrorArticle(sceneObject);
		if (articleId && sceneObject && this.sceneItems.size > 1) {
			const relationMap = this.connectionManager.getAllRelations(articleId, {
				valid: true,
				available: true,
			});
			const worldPosition = this.connectionManager.getWorldPosition(sceneObject);
			relationMap.forEach((relations, parentId) => {
				const parent = this.sceneItems.get(parentId);
				if (parent && parentId !== sceneObject.id) {
					const collidingObjects = this.getCollidingObjects(parent);
					if (!collidingObjects.length) {
						relations.forEach((relation) => {
							if (this.rangeSettings.connectionsToExcludeFromMiddleDrag?.includes(relation.child)) {
								return;
							}
							const worldTransform = this.getWorldTransform(articleId, parent, relation);
							if (worldTransform.position) {
								connections.push(TransformVector.distance(worldPosition, worldTransform.position));
							}
						});
					}
				}
			});
		}
		if (connections.length) {
			return connections.sort((a, b) => b - a).pop();
		}
		return undefined;
	}

	private getWorldTransform(
		articleId: string,
		parent: SceneObject,
		relation: ConnectionRelation
	): Transform {
		const localTransform = this.connectionManager.getConnectedTransform(
			articleId,
			parent.articleId,
			relation
		);
		return this.connectionManager.getRelativeWorldTransformFromObject(localTransform, parent);
	}

	/**
	 * Get colliding objects for sceneObject.
	 *
	 * @param sceneObject Scene Object.
	 */
	private getCollidingObjects(sceneObject: SceneObject): string[] {
		const gridBoxes = this.gridTools.createGridBoxes(this.sceneItems, 0, [sceneObject.id]);
		const bounds = this.gridTools.getBoundsName(sceneObject.articleId);
		const itemGridBox = this.gridTools.createGridBoxFromObject(sceneObject, bounds, 4);

		return this.gridTools.checkBoxIntersections(itemGridBox, gridBoxes);
	}
}
